import { AnalyticsBrowser } from "@segment/analytics-next"
import type { Plugin, Analytics } from "@segment/analytics-next"

import { fetchCartId, isConsolidatedProduct } from "./shop"

let analytics: Analytics | null = null

if (process.env.GATSBY_SEGMENT_WRITE_KEY && typeof window !== "undefined") {
  AnalyticsBrowser.load({
    writeKey: process.env.GATSBY_SEGMENT_WRITE_KEY,
  }).then(([response]) => {
    analytics = response
    window.analytics = response
    analytics.register(socialCookies)
    analytics.register(traits)
  })
}

export const socialCookies: Plugin = {
  name: "Add social cookie values to all Events",
  type: "before",
  version: "1.0.0",

  isLoaded: () => true,
  load: () => Promise.resolve(),
  track: (ctx) => {
    const fbp = getCookie("_fbp")
    if (fbp) {
      ctx.event.context!.fbp = fbp
    }
    const fbc = getCookie("_fbc")
    if (fbc) {
      ctx.event.context!.fbc = fbc
    }
    const gclid = getCookie("_gcl_au")
    if (gclid) {
      ctx.event.context!.gclid = gclid
    }
    return ctx
  },

  page: (ctx) => {
    const fbp = getCookie("_fbp")
    if (fbp) {
      ctx.event.context!.fbp = fbp
    }
    const fbc = getCookie("_fbc")
    if (fbc) {
      ctx.event.context!.fbc = fbc
    }
    const gclid = getCookie("_gcl_au")
    if (gclid) {
      ctx.event.context!.gclid = gclid
    }
    return ctx
  },
}

const fetchUserIpAddress = async (): Promise<string | null> => {
  try {
    const wauCookie = getCookie("wau")
    if (!wauCookie) {
      return null
    }

    const response = await fetch(
      `${process.env.GATSBY_SHOP_API_URL}/signup/hello.json`
    )
    if (!response.ok) {
      console.warn(
        `Failed to fetch IP address: ${response.status} ${response.statusText}`
      )
      return null
    }

    const data = await response.json()
    return data.ip_address || null
  } catch (error) {
    console.warn("Error fetching IP address:", error)
    return null
  }
}

export const traits: Plugin = {
  name: "Add Traits to all Events",
  type: "before",
  version: "1.0.0",

  isLoaded: () => true,
  load: () => Promise.resolve(),
  track: async (ctx) => {
    if (analytics) {
      const user = analytics.user()
      if (user) {
        const traits = analytics.user().traits()
        ctx.event.context!.traits = traits

        const ipAddress = await fetchUserIpAddress()
        if (ipAddress && ctx.event.context!.traits) {
          ctx.event.context!.traits.ip_address = ipAddress
        }
      }
    }

    return ctx
  },

  page: async (ctx) => {
    if (analytics) {
      const user = analytics.user()
      if (user) {
        const traits = analytics.user().traits()
        ctx.event.context!.traits = traits

        const ipAddress = await fetchUserIpAddress()
        if (ipAddress && ctx.event.context!.traits) {
          ctx.event.context!.traits.ip_address = ipAddress
        }
      }
    }
    return ctx
  },
}

// MARK: used in v2.

export const trackSegmentEvent = (
  event: string,
  data: Record<string, any> = {}
): void => {
  if (typeof window === "undefined" || !window.analytics) {
    return console.warn(`Failed to track event: segment is undefined`)
  }

  return window.analytics.track(event, data)
}

export const identifySegmentUser = (
  userId?: string,
  traits: Record<string, any> = {}
): void => {
  if (typeof window === "undefined" || !window.analytics) {
    return console.warn(`Failed to identify user: segment is undefined`)
  }

  return window.analytics.identify(userId, traits)
}

export const trackSegmentLinkEvent = (
  element: React.ReactNode,
  event: string,
  data: Record<string, any> = {}
): void => {
  if (typeof window === "undefined" || !window.analytics) {
    return console.warn(`Failed to track event: segment is undefined`)
  }

  return window.analytics.trackLink(element, event, data)
}

export const pushToDataLayer = (data: Record<string, any>): void => {
  if (typeof window === "undefined" || !window.dataLayer) {
    return console.warn(
      `Failed to push to data layer: window.dataLayer is undefined`
    )
  }

  return window.dataLayer.push(data)
}

export const getCookie = (name: string) => {
  return document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`))
    ?.split("=")[1]
}

export const isMobileDevice = () => {
  const conditions = [
    "Tablet",
    "Mobile",
    "Mobile Safari",
    "IEMobile/xyz",
    "Mobile/xyz",
    "Edge/ ",
  ]

  return conditions.some((condition) => navigator.userAgent.includes(condition))
}

export const trackAddToCart = (addedProduct: YocoCom.ConsolidatedCartItem) => {
  const cartId = fetchCartId()

  trackSegmentEvent("Product Added", {
    cart_id: cartId,
    product_id: addedProduct.id,
    sku: isConsolidatedProduct(addedProduct)
      ? addedProduct.sku
      : addedProduct.bundleCode,
    category: isConsolidatedProduct(addedProduct)
      ? addedProduct.type
      : "bundle",
    price: (Number(addedProduct.price) * addedProduct.quantity).toFixed(2),
    quantity: addedProduct.quantity,
  })
}
